// api/candleDepth.ts — first-load candle DEPTH policy (design §4.4, F1).
//
// Ported from the OLD chart's useHistoricalData.ts:28,38-55. The modular store
// is 1m-base-ONLY and derives every higher TF client-side via aggregateCandles,
// so a fixed 720 1m bars yields far too few bars at 15m/1h/4h to warm long
// indicators (~48 @15m / ~12 @1h / ~3 @4h) — this starved EMA200/Ichimoku-52/
// BBands-20 and was the load-bearing root of "first load too little/slow" AND
// "indicators broadly wrong".
//
// Two-step policy:
//   1. targetCandles(intervalMinutes, maxMaPeriod) — the OLD per-interval depth:
//      ~7 days visible, or MA-warmup depth (maxMaPeriod·MA_SPARSITY_FACTOR),
//      whichever is larger, clamped 200..5000.
//   2. requiredBaseCandles(intervalMinutes, maxMaPeriod) — because the modular
//      store fetches the 1m base and aggregates client-side, the number of 1m
//      bars to fetch = targetCandles(interval, …)·interval (the 1m bars that
//      aggregate up to ~7d of the panel's TF), capped at SERVER_MAX_LIMIT 10000.

import { useSettingsStore } from '../stores/settingsStore';
import { useDashboardStore } from '../stores/dashboardStore';

/**
 * Sparsity factor applied when fetching extra history for long-period MAs.
 *
 * A sparse pair (e.g. WARD/USD 1H) may have only 1 real trade every N calendar
 * slots — the rest are synthetic gap-fill candles skipped by the MA compute
 * pass. Fetching `period * MA_SPARSITY_FACTOR` rows gives a good chance of
 * accumulating `period` real closes even on thin markets. Empirically 3× covers
 * pairs with ~33% real-candle density.
 */
export const MA_SPARSITY_FACTOR = 3;

/** Server-side hard cap on /api/candles (candleRoutes.ts MAX_LIMIT). Mirror it
 *  on the client so a heavy first fetch for BTC/ETH stays bounded. */
export const SERVER_MAX_LIMIT = 10000;

/**
 * Per-interval target candle count for first load — scales with interval so
 * higher timeframes don't pull years of history (which distorts the Y-axis for
 * assets with large historical price swings). When long-period MAs are enabled
 * the depth is increased so sparse pairs accumulate enough real (non-synthetic)
 * closes to warm those indicators.
 */
export function targetCandles(
  intervalMinutes: number,
  maxMaPeriod: number,
): number {
  // Target ~7 days of visible data for all intervals.
  const targetDays = 7;
  const visibleCandles = Math.ceil((targetDays * 24 * 60) / intervalMinutes);

  // Depth required to warm the longest enabled MA on a sparse pair. We multiply
  // by MA_SPARSITY_FACTOR to account for synthetic gaps that are skipped during
  // indicator computation but count toward the raw row total.
  const maWarmupCandles =
    maxMaPeriod > 0 ? maxMaPeriod * MA_SPARSITY_FACTOR : 0;

  const depth = Math.max(visibleCandles, maWarmupCandles);
  // At least 200 bars, at most 5000 (per-TF cap, OLD parity).
  return Math.max(200, Math.min(depth, 5000));
}

/**
 * Number of 1m BASE bars to fetch so that, after client-side aggregation to the
 * panel's interval, ~7d (or MA-warmup) of that TF is available. Because the
 * store is 1m-base-only, the 1m count = targetCandles(interval,…)·interval.
 *
 * For interval=1 this is just targetCandles(1,…) (≈3.5d of 1m at the 5000 cap).
 * Capped at SERVER_MAX_LIMIT so the worst case (e.g. 4h × its target) stays
 * bounded and never exceeds what /api/candles will serve.
 */
export function requiredBaseCandles(
  intervalMinutes: number,
  maxMaPeriod: number,
): number {
  const interval = intervalMinutes > 0 ? intervalMinutes : 1;
  const perTf = targetCandles(interval, maxMaPeriod);
  return Math.min(perTf * interval, SERVER_MAX_LIMIT);
}

/**
 * Warmup-period floor for each non-MA price overlay that needs a long lookback
 * to produce values. Enabling one of these (even with NO moving average on)
 * must deepen the fetch, or its cloud/band fill renders thin or empty —
 * especially at higher TFs where each aggregated bar consumes many 1m bars.
 *
 *   • ichimoku — Senkou B = 52-period high/low, displaced 26 bars forward → 78.
 *   • bbands   — 20-period SMA + stddev.
 *   • adx      — 14-period (Wilder smoothing wants ~2×).
 *
 * These are intentionally per-INDICATOR constants (not config-driven) because
 * the modular overlays use the fixed calculator periods in computeIndicators
 * (IchimokuCalculator(9,26,52), BollingerBandsCalculator(20,2), …).
 */
const OVERLAY_WARMUP: Record<string, number> = {
  ichimoku: 52 + 26,
  bbands: 20,
  adx: 28,
  vwap: 0, // session-anchored — no long lookback
  psar: 0,
};

/**
 * The longest enabled WARMUP period a fetch for this symbol must cover, across
 * EVERY price-chart panel in the active layout (the store is symbol-keyed and
 * single-flight, so one symbol's fetch must satisfy all panels showing it) plus
 * the global EMA/SMA toggles. Read live via getState() — this runs in a
 * fetcher/store path, not a React component, so a hook subscription would be
 * wrong here. Returns 0 when nothing warmup-hungry is enabled.
 *
 * NOTE: the modular overlays are gated by PER-PANEL config.indicators (bbands /
 * ichimoku / adx booleans), NOT settingsStore — so the depth policy must read
 * the dashboard layout, not just the global MA toggles. (Before this, enabling
 * only Ichimoku/BBands left the fetch shallow and the fill failed to render.)
 */
export function maxEnabledWarmupPeriod(): number {
  const { enabledEmas, enabledSmas } = useSettingsStore.getState();
  let max = Math.max(0, ...enabledEmas, ...enabledSmas);

  const dash = useDashboardStore.getState();
  const layout = dash.layouts[dash.activeLayout];
  if (layout) {
    for (const panel of Object.values(layout.panelConfigs)) {
      if (panel.type !== 'price-chart') continue;
      const ind = (panel.config as { indicators?: Record<string, unknown> }).indicators;
      if (!ind) continue;
      for (const [key, floor] of Object.entries(OVERLAY_WARMUP)) {
        if (ind[key] && floor > max) max = floor;
      }
      // Per-panel EMA/SMA arrays (config.indicators.emas/smas) also count.
      const emas = (ind as { emas?: number[] }).emas;
      const smas = (ind as { smas?: number[] }).smas;
      if (Array.isArray(emas) && emas.length) max = Math.max(max, ...emas);
      if (Array.isArray(smas) && smas.length) max = Math.max(max, ...smas);
    }
  }
  return max;
}

/** @deprecated MA-only alias kept for callers; prefer maxEnabledWarmupPeriod(). */
export function maxEnabledMaPeriod(): number {
  return maxEnabledWarmupPeriod();
}

/**
 * Re-deepen decision (Task 2 — interval re-deepen). ensureLoaded is single-flight
 * per SYMBOL, so the FIRST subscriber's interval fixes the fetch depth. If a 1m
 * (or book/trades) panel seeds first, then the user raises the chart to 4h, the
 * already-'ready' base array is too shallow for the aggregated 4h view's
 * indicators — it stays shallow until a reload.
 *
 * Given the interval a NEW subscriber requests and the seedDepth currently loaded
 * for the symbol, return the deeper 1m base-candle target if a deeper fetch is
 * warranted, or null when the loaded depth already covers it. The result is
 * clamped at SERVER_MAX_LIMIT, so a symbol already at the cap returns null
 * (prevents a re-deepen loop at the ceiling).
 */
export function deeperBaseCandles(
  requestedInterval: number,
  loadedSeedDepth: number,
): number | null {
  const required = requiredBaseCandles(requestedInterval, maxEnabledWarmupPeriod());
  // Only deepen on a STRICT increase; equal/shallower needs nothing. A symbol
  // already at SERVER_MAX_LIMIT can never deepen further (required is capped).
  return required > loadedSeedDepth ? required : null;
}
